<template>
  <div class="face-simple-page" v-loading="loading">
    <div v-if="!loading && errorMsg" class="empty-state">
      <el-empty :description="errorMsg">
        <el-button type="primary" round @click="fetchData">重新加载</el-button>
      </el-empty>
    </div>

    <div v-else-if="!loading && hasReport" class="page-body">
      <div class="report-card">
        <div v-if="hasTopSection" class="report-top">
          <div v-if="hasHealthScore" class="score-panel">
            <div class="score-label">健康评分</div>
            <div class="dashboard">
              <div class="dashboard-scale">
                <span
                  v-for="item in dashboardTicks"
                  :key="item.index"
                  class="dashboard-tick"
                  :class="{
                    'dashboard-tick--active': item.active,
                    'dashboard-tick--major': item.isMajor
                  }"
                  :style="item.style"
                ></span>
                <div
                  class="dashboard-needle-wrap"
                  :style="{ transform: `translateX(-50%) rotate(${dashboardNeedleDeg}deg)` }"
                >
                  <div class="dashboard-needle"></div>
                </div>
                <div class="dashboard-center-dot"></div>
              </div>
              <div class="score-value">
                <span class="score-num">{{ normalizedHealthScore }}</span>
                <span class="score-unit">分</span>
              </div>
              <div class="dashboard-extrema">
                <span>0</span>
                <span>100</span>
              </div>
            </div>
          </div>

          <div v-if="hasCoreInfo" class="core-list">
            <div v-if="hasPhysique || hasPhysiqueSubType" class="core-item">
              <div class="report-card__title">体质倾向</div>
              <div
                v-if="hasPhysique"
                class="core-item-value"
                :class="{ 'core-item-value--danger': !isPeacefulPhysique }"
              >
                {{ faceReport.physique }}
              </div>
              <div v-if="hasPhysiqueSubType" class="core-sub-item">
                <span class="core-sub-label">兼</span>
                <span class="core-sub-value">{{ faceReport.physiqueSubTypeText }}</span>
              </div>
            </div>

            <div v-if="hasSyndrome" class="core-item">
              <div class="report-card__title">症形判断</div>
              <div class="core-item-value core-item-value--warning">
                {{ faceReport.syndrome }}
              </div>
            </div>
          </div>
        </div>

        <div v-if="simpleCategoryEntries.length" class="section">
          <div class="report-card__title">症状类型</div>
          <div
            v-for="(section, index) in simpleCategoryEntries"
            :key="`category-${index}`"
            class="category-card"
          >
            <div class="category-head">
              <div class="category-title">{{ section.name }}</div>
            </div>
            <div class="chip-list">{{ section.items.join('、') }}</div>
            <div v-if="getCategoryGoods(section).length" class="category-goods-wrap">
              <div class="category-goods-title">商品推荐</div>
              <div class="category-goods-list">
                <div
                  v-for="(item, goodsIndex) in getCategoryGoods(section)"
                  :key="`category-goods-${index}-${goodsIndex}`"
                  class="category-goods-card"
                  @click="openGoodsDetail(item)"
                >
                  <div class="category-goods-image-wrap">
                    <img
                      :src="getGoodsImage(item.coverImage || item.image || item.img)"
                      :alt="item.name || item.goodsName || item.title || '商品'"
                      class="category-goods-image"
                    >
                  </div>
                  <div class="category-goods-info">
                    <div class="category-goods-name">{{ item.name || item.goodsName || item.title || '--' }}</div>
                    <div class="category-goods-price-row">
                      <span class="category-goods-price">￥{{ formatPrice(getGoodsPrice(item)) }}</span>
                      <span
                        v-if="hasGoodsLinePrice(item)"
                        class="category-goods-line-price"
                      >
                        ￥{{ formatPrice(getGoodsLinePrice(item)) }}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div v-if="faceReport.typicalSymptoms.length" class="section">
          <div class="report-card__title">典型症状（{{ faceReport.typicalSymptoms.length }}）</div>
          <div class="chip-list">{{ faceReport.typicalSymptoms.join('、') }}</div>
        </div>

        <div class="report-footer">
          本报告仅供参考，不能替代专业医疗诊断，如有不适请及时就医。
        </div>

        <div class="detail-report-section">
          <button type="button" class="detail-report-button" @click="goToIndex">查看完整页面</button>
        </div>
      </div>
    </div>

    <div v-else-if="!loading" class="empty-state">
      <el-empty description="暂无面诊简版报告数据">
        <el-button type="primary" round @click="fetchData">重新加载</el-button>
      </el-empty>
    </div>
  </div>
</template>

<script>
import { getCodeProjectCg, recommendationList } from '@/api/signUp'

function createEmptyFaceReport() {
  return {
    ready: false,
    physique: '',
    physiqueType: '',
    physiqueSubType: '',
    physiqueSubTypeText: '',
    syndrome: '',
    healthScore: null,
    typicalSymptoms: [],
    keySymptomCategories: {}
  }
}

export default {
  name: 'FaceSimpleView',
  data() {
    return {
      loading: true,
      errorMsg: '',
      id: '',
      clinicId: '',
      faceReport: createEmptyFaceReport(),
      categoryRecommendationGoods: {}
    }
  },
  computed: {
    goodsImageBaseUrl() {
      return (process.env.VUE_IMG_URL || 'https://cos.dexintangjiankang.cn').replace(/\/$/, '')
    },
    hasReport() {
      return !!(this.faceReport && this.faceReport.ready)
    },
    normalizedHealthScore() {
      const score = Number(this.faceReport.healthScore)
      if (!Number.isFinite(score)) {
        return null
      }
      return Math.max(0, Math.min(100, Math.round(score)))
    },
    hasHealthScore() {
      return this.normalizedHealthScore !== null
    },
    dashboardNeedleDeg() {
      return this.hasHealthScore ? this.normalizedHealthScore * 1.8 - 90 : -90
    },
    dashboardTicks() {
      const tickCount = 13
      const centerX = 120
      const centerY = 118
      const radius = 88
      const activeTickIndex = this.hasHealthScore
        ? Math.round((this.normalizedHealthScore / 100) * (tickCount - 1))
        : -1

      return Array.from({ length: tickCount }, (_, index) => {
        const angle = 180 + (180 / (tickCount - 1)) * index
        const radian = (angle * Math.PI) / 180
        const left = centerX + radius * Math.cos(radian)
        const top = centerY + radius * Math.sin(radian)

        return {
          index,
          active: index <= activeTickIndex,
          isMajor: index % 3 === 0 || index === tickCount - 1,
          style: `left:${left.toFixed(2)}px;top:${top.toFixed(2)}px;transform:translate(-50%, -50%) rotate(${angle + 90}deg);`
        }
      })
    },
    hasPhysique() {
      return !!(this.faceReport && typeof this.faceReport.physique === 'string' && this.faceReport.physique.trim())
    },
    hasPhysiqueSubType() {
      return !!(
        this.faceReport
        && typeof this.faceReport.physiqueSubTypeText === 'string'
        && this.faceReport.physiqueSubTypeText.trim()
      )
    },
    hasSyndrome() {
      return !!(this.faceReport && typeof this.faceReport.syndrome === 'string' && this.faceReport.syndrome.trim())
    },
    hasCoreInfo() {
      return this.hasPhysique || this.hasPhysiqueSubType || this.hasSyndrome
    },
    hasTopSection() {
      return this.hasHealthScore || this.hasCoreInfo
    },
    isPeacefulPhysique() {
      const type = this.faceReport && this.faceReport.physiqueType != null
        ? String(this.faceReport.physiqueType).trim()
        : ''
      if (type) {
        return type === '1'
      }
      const physique = this.faceReport && typeof this.faceReport.physique === 'string'
        ? this.faceReport.physique.trim()
        : ''
      return !physique || physique === '平和质'
    },
    simpleCategoryEntries() {
      const source = this.faceReport && this.faceReport.keySymptomCategories
      if (!source || typeof source !== 'object') {
        return []
      }
      return Object.keys(source)
        .map((name) => ({
          name,
          items: Array.isArray(source[name])
            ? source[name].filter((item) => typeof item === 'string' && item.trim())
            : []
        }))
        .filter((item) => item.items.length)
    }
  },
  created() {
    this.fetchData()
  },
  methods: {
    async fetchData() {
      this.id = this.$route.query.id || ''

      if (!this.id) {
        this.errorMsg = '缺少报告 ID 参数'
        this.loading = false
        return
      }

      this.loading = true
      this.errorMsg = ''
      this.clinicId = ''
      this.faceReport = createEmptyFaceReport()
      this.categoryRecommendationGoods = {}

      try {
        const res = await getCodeProjectCg({ id: this.id })
        const response = res && res.data ? res.data : {}

        if (String(response.code) !== '200' || !response.data) {
          this.errorMsg = response.msg || '获取报告失败'
          return
        }

        const nextReport = this.buildFaceReport(response.data)
        this.clinicId = response.data && response.data.clinicId != null ? String(response.data.clinicId).trim() : ''
        this.faceReport = nextReport
        await this.fetchCategoryRecommendationGoods()

        if (!nextReport.ready) {
          this.errorMsg = '未获取到面诊简版报告数据'
        }
      } catch (error) {
        console.error('获取面诊简版报告失败：', error)
        this.errorMsg = '网络请求失败，请稍后重试'
      } finally {
        this.loading = false
      }
    },
    buildFaceReport(reportData) {
      const result = this.safeParse(reportData && reportData.result, null)
      const resultSimple = this.extractSimplePayload(reportData && reportData.resultSimple)
      const faceSource = resultSimple || result
      const normalizedFace = this.normalizeFaceReport(faceSource)
      const answerSheetPatch = this.buildAnswerSheetFacePatch(reportData && reportData.answerSheet)

      if (!normalizedFace && !answerSheetPatch) {
        return createEmptyFaceReport()
      }

      return {
        ...createEmptyFaceReport(),
        ...(normalizedFace || {}),
        ...(answerSheetPatch || {}),
        ready: !!(
          (normalizedFace && normalizedFace.ready)
          || (answerSheetPatch && (answerSheetPatch.physique || answerSheetPatch.physiqueSubTypeText))
        )
      }
    },
    safeParse(value, fallback = null) {
      if (value == null || value === '') {
        return fallback
      }
      if (typeof value === 'object') {
        return value
      }
      if (typeof value !== 'string') {
        return fallback
      }
      try {
        return JSON.parse(value)
      } catch (error) {
        return fallback
      }
    },
    extractSimplePayload(raw) {
      const source = this.safeParse(raw, raw)
      if (!source || typeof source !== 'object') {
        return null
      }

      let current = source
      for (let index = 0; index < 2; index += 1) {
        if (!current || typeof current !== 'object') {
          break
        }
        const next = this.safeParse(current.data, current.data)
        if (next && typeof next === 'object') {
          current = next
        } else {
          break
        }
      }

      return current && typeof current === 'object' ? current : null
    },
    normalizeFaceReport(raw) {
      if (!raw || typeof raw !== 'object') {
        return null
      }

      const typicalSymptoms = Array.isArray(raw.typicalSymptoms)
        ? raw.typicalSymptoms.filter((item) => typeof item === 'string' && item.trim())
        : (typeof raw.typical_symptom === 'string'
          ? raw.typical_symptom.split(/[、，,；;\n]+/).map((item) => item.trim()).filter(Boolean)
          : [])

      const keySymptomCategories = raw.keySymptomCategories && typeof raw.keySymptomCategories === 'object'
        ? raw.keySymptomCategories
        : {}

      const healthScore = Number.isFinite(Number(raw.healthScore))
        ? Number(raw.healthScore)
        : (Number.isFinite(Number(raw.score)) ? Number(raw.score) : null)

      const report = {
        ready: !!(
          raw.physique
          || raw.physique_name
          || raw.syndrome
          || raw.syndrome_name
          || healthScore !== null
          || typicalSymptoms.length
          || Object.keys(keySymptomCategories).length
        ),
        physique: raw.physique || raw.physique_name || '',
        physiqueType: raw.physiqueType || raw.type || '',
        physiqueSubType: raw.physiqueSubType || raw.subType || '',
        physiqueSubTypeText: raw.physiqueSubTypeText || raw.subTypeText || '',
        syndrome: raw.syndrome || raw.syndrome_name || '',
        healthScore,
        typicalSymptoms,
        keySymptomCategories
      }

      return report.ready ? report : null
    },
    normalizeAnswerSheetPayload(raw) {
      if (Array.isArray(raw)) {
        return raw.find((item) => item && typeof item === 'object' && (item.typeText || item.subTypeText)) || null
      }
      return raw && typeof raw === 'object' ? raw : null
    },
    buildAnswerSheetFacePatch(raw) {
      const source = this.normalizeAnswerSheetPayload(raw)
      if (!source) {
        return null
      }

      const physique = typeof source.typeText === 'string' ? source.typeText.trim() : ''
      const physiqueSubTypeText = typeof source.subTypeText === 'string' ? source.subTypeText.trim() : ''
      if (!physique && !physiqueSubTypeText) {
        return null
      }

      return {
        physique,
        physiqueType: source.type || '',
        physiqueSubType: source.subType || '',
        physiqueSubTypeText
      }
    },
    normalizeRecommendationData(res) {
      const response = res && res.data ? res.data : {}
      if (Array.isArray(response)) {
        return response
      }
      if (response && response.code != null && String(response.code) !== '200') {
        return []
      }
      if (Array.isArray(response.data)) {
        return response.data
      }
      if (Array.isArray(response.list)) {
        return response.list
      }
      if (Array.isArray(response.data && response.data.list)) {
        return response.data.list
      }
      return []
    },
    async fetchCategoryRecommendationGoods() {
      const entries = this.simpleCategoryEntries
      if (!this.faceReport.ready || !entries.length) {
        this.categoryRecommendationGoods = {}
        return
      }

      const tasks = entries.map(async (section) => {
        try {
          const res = await recommendationList(section.items || [], { silentError: true })
          return [section.name, this.normalizeRecommendationData(res)]
        } catch (error) {
          return [section.name, []]
        }
      })

      const categoryEntries = await Promise.all(tasks)
      const nextMap = {}
      categoryEntries.forEach(([name, list]) => {
        nextMap[name] = list
      })
      this.categoryRecommendationGoods = nextMap
    },
    getCategoryGoods(section) {
      if (!section || !section.name) {
        return []
      }
      return Array.isArray(this.categoryRecommendationGoods[section.name])
        ? this.categoryRecommendationGoods[section.name]
        : []
    },
    getGoodsPrice(item) {
      if (!item || typeof item !== 'object') {
        return 0
      }
      return item.memberPrice || item.price || item.salePrice || item.marketPrice || 0
    },
    getGoodsLinePrice(item) {
      if (!item || typeof item !== 'object') {
        return ''
      }
      return item.linePrice || item.marketPrice || ''
    },
    hasGoodsLinePrice(item) {
      const salePrice = Number(this.getGoodsPrice(item))
      const linePrice = Number(this.getGoodsLinePrice(item))
      return Number.isFinite(linePrice) && linePrice > 0 && linePrice !== salePrice
    },
    formatPrice(price) {
      const num = Number(price)
      if (Number.isNaN(num)) {
        return '0.00'
      }
      return num.toFixed(2)
    },
    getGoodsImage(url) {
      if (!url) {
        return ''
      }
      const imageUrl = String(url).trim()
      if (/^(https?:)?\/\//i.test(imageUrl) || imageUrl.startsWith('data:')) {
        return imageUrl
      }
      return `${this.goodsImageBaseUrl}/${imageUrl.replace(/^\/+/, '')}`
    },
    openGoodsDetail(item) {
      const goodsId = item && (item.goodsId || item.id)
      console.log(goodsId)
      if (!goodsId) {
        return
      }

      if (window.wx && window.wx.miniProgram && typeof window.wx.miniProgram.navigateTo === 'function') {
        const extraParams = this.clinicId ? `&pType=1&pId=${encodeURIComponent(this.clinicId)}` : ''
        window.wx.miniProgram.navigateTo({
          url: `/pages/goods-detail/goods-detail?id=${goodsId}${extraParams}`
        })
        return
      }
      if (this.$message && typeof this.$message.warning === 'function') {
        this.$message.warning('请在微信环境中打开商品详情')
      }
    },
    goToIndex() {
      this.$router.push({
        path: '/',
        query: this.id ? { id: this.id } : {}
      })
    }
  }
}
</script>

<style scoped>
.face-simple-page {
  min-height: 100vh;
  padding: 20px;
  background: #f8fafc;
  box-sizing: border-box;
}
.page-body { padding: 0; }
.report-card {
  max-width: 980px; margin: 0 auto; padding: 10px 28px 28px;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 22px;
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.04);
  box-sizing: border-box;
}
.report-top {
  display: flex; gap: 32px; align-items: stretch; padding: 24px 0 28px;
  border-bottom: 1px solid #e6dfd5;
}
.score-panel { width: 280px; padding: 0 14px 0 0; text-align: center; color: #1e3a8a; }
.score-label {
  font-size: 19px;
  font-weight: 700;
  color: #1f1f1f;
}
.report-card__title {
  position: relative;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 0 14px;
  font-size: 19px;
  font-weight: 700;
  color: #1f1f1f;
  background: transparent;
  border-bottom: 0;
}
.dashboard { margin-top: 12px; }
.dashboard-scale { position: relative; width: 240px; height: 142px; margin: 0 auto; }
.dashboard-tick {
  position: absolute; width: 8px; height: 24px; border-radius: 999px;
  background: rgba(37, 99, 235, 0.16); box-shadow: 0 4px 10px rgba(37, 99, 235, 0.08);
}
.dashboard-tick--major { width: 10px; height: 30px; }
.dashboard-tick--active {
  background: linear-gradient(180deg, #93c5fd 0%, #3b82f6 100%);
  box-shadow: 0 4px 12px rgba(59, 130, 246, 0.2);
}
.dashboard-needle-wrap { position: absolute; left: 50%; bottom: 18px; width: 8px; height: 82px; transform-origin: center bottom; }
.dashboard-needle {
  width: 8px; height: 82px; margin: 0 auto; border-radius: 999px;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.98) 0%, #93c5fd 42%, #3b82f6 100%);
  box-shadow: 0 6px 14px rgba(37, 99, 235, 0.16);
}
.dashboard-center-dot {
  position: absolute; left: 50%; bottom: 10px; width: 20px; height: 20px;
  border-radius: 50%; background: #fff; box-shadow: 0 6px 16px rgba(37, 99, 235, 0.14); transform: translateX(-50%);
}
.score-value { margin-top: -4px; display: flex; align-items: baseline; justify-content: center; gap: 6px; }
.score-num { font-size: 52px; line-height: 1; font-weight: 700; color: #2563eb; }
.score-unit, .dashboard-extrema, .core-sub-label { color: #64748b; }
.score-unit { font-size: 16px; }
.dashboard-extrema { width: 240px; margin: 8px auto 0; display: flex; justify-content: space-between; font-size: 13px; }
.core-list { flex: 1; min-width: 0; display: flex; flex-direction: column; gap: 14px; }
.core-item {
  flex: 1; padding: 18px 20px;
  border: 1px solid #edf2f7;
  border-radius: 18px;
  background: #f8fafc;
}
.core-item + .core-item {
  border-top: 0;
}
.core-item-value { margin-top: 8px; font-size: 24px; line-height: 34px; color: #0f172a; font-weight: 600; }
.core-item-value--danger, .core-sub-value, .category-title { color: #ef4444; }
.core-item-value--warning { color: #f59e0b; }
.core-sub-item { margin-top: 8px; display: flex; align-items: center; gap: 10px; }
.core-sub-label { min-width: 28px; font-size: 14px; line-height: 20px; }
.core-sub-value { font-size: 16px; line-height: 24px; font-weight: 600; }
.section {
  margin-top: 0; padding: 24px 0 0;
  border-top: 1px solid #e6dfd5;
}
.report-card__title { margin-bottom: 14px; }
.category-card {
  padding: 18px 20px;
  border: 1px solid #edf2f7;
  border-radius: 18px;
  background: #f8fafc;
}
.category-card + .category-card { border-top: 0; }
.category-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; margin-bottom: 12px; }
.category-title { font-size: 18px; font-weight: 600; }
.category-count {
  height: 30px; padding: 0 12px; border-radius: 999px; display: inline-flex; align-items: center;
  background: rgba(37, 99, 235, 0.12); color: #1d4ed8; font-size: 13px;
}
.chip-list {
  font-size: 14px;
  line-height: 24px;
  color: #334155;
  word-break: break-word;
}
.category-goods-wrap {
  margin-top: 16px;
  padding-top: 16px;
  border-top: 1px dashed #d9e2ec;
}
.category-goods-list::-webkit-scrollbar {
  height: 6px;
}
.category-goods-list::-webkit-scrollbar-thumb {
  border-radius: 999px;
  background: rgba(148, 163, 184, 0.35);
}
.category-goods-list::-webkit-scrollbar-track {
  background: transparent;
}
.category-goods-card {
  display: flex;
  flex: 0 0 280px;
  gap: 12px;
  padding: 16px 16px 16px 0;
  margin-right: 16px;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-right: 1px solid #e6dfd5;
  border-radius: 0;
  transition: opacity 0.2s ease, transform 0.2s ease;
  scroll-snap-align: start;
  border-top: 0;
}
.category-goods-title {
  margin-bottom: 12px;
  font-size: 16px;
  line-height: 24px;
  font-weight: 600;
  color: #2563eb;
}
.category-goods-list {
  display: flex;
  flex-direction: row;
  gap: 0;
  overflow-x: auto;
  overflow-y: hidden;
  padding-bottom: 4px;
  scroll-snap-type: x proximity;
  -webkit-overflow-scrolling: touch;
}
.category-goods-card:hover {
  opacity: 0.85;
  transform: translateY(-1px);
}
.category-goods-card:last-child {
  margin-right: 0;
  padding-right: 0;
  border-right: 0;
}
.category-goods-image-wrap {
  width: 92px;
  height: 92px;
  flex-shrink: 0;
  border-radius: 6px;
  overflow: hidden;
  background: #f1ece6;
}
.category-goods-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
}
.category-goods-info {
  min-width: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}
.category-goods-name {
  font-size: 17px;
  font-weight: 600;
  color: #23201e;
  line-height: 1.5;
  word-break: break-word;
}
.category-goods-price-row {
  display: flex;
  align-items: baseline;
  gap: 8px;
  margin-top: 12px;
}
.category-goods-price {
  font-size: 21px;
  font-weight: 700;
  color: #b9413b;
}
.category-goods-line-price {
  font-size: 14px;
  color: #a59b90;
  text-decoration: line-through;
}
.report-footer {
  margin-top: 0; padding: 24px 0 0; border-top: 1px solid #e6dfd5;
  font-size: 14px; line-height: 22px; color: #991b1b;
}
.detail-report-section {
  padding: 24px 0 0;
  display: flex;
  justify-content: center;
}
.detail-report-button {
  min-width: 220px;
  padding: 14px 28px;
  border: 1px solid #2f6bff;
  border-radius: 999px;
  background: #2f6bff;
  color: #fff;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.2;
  cursor: pointer;
  transition: background-color 0.2s ease, color 0.2s ease, box-shadow 0.2s ease, transform 0.2s ease;
  box-shadow: 0 10px 20px rgba(47, 107, 255, 0.18);
}
.detail-report-button:focus {
  outline: none;
}
.detail-report-button:hover {
  background: #fff;
  color: #2f6bff;
  transform: translateY(-1px);
}
.empty-state { min-height: 100vh; display: flex; align-items: center; justify-content: center; }
@media (max-width: 768px) {
  .face-simple-page { padding: 12px; }
  .page-body { padding: 0; }
  .report-card { padding: 0 16px 18px; border-radius: 18px; }
  .report-top { flex-direction: column; gap: 20px; padding: 20px 0 24px; }
  .score-panel { width: 100%; padding-right: 0; }
  .core-item,
  .category-card { padding: 16px; border-radius: 16px; }
  .detail-report-button {
    width: 100%;
    min-width: 0;
  }
  .category-goods-card {
    flex-basis: 240px;
    margin-right: 12px;
    padding-right: 12px;
  }
  .category-goods-card {
    align-items: flex-start;
  }
  .category-goods-image-wrap {
    width: 84px;
    height: 84px;
  }
  .category-goods-name {
    font-size: 15px;
  }
  .category-goods-price {
    font-size: 18px;
  }
}
</style>
